import { ReactNode } from "react";
import { createPortal } from "react-dom";

import Button from "components/Button";
import { CloseIcon } from "components/icons";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

interface ModalProps {
  id?: string;
  open?: boolean;
  wrapperClassName?: string;
  className?: string;
  size?: "sm" | "md" | "2xl";
  titleClassName?: string;
  title?: React.ReactNode;
  contentClassName?: string;
  children?: React.ReactNode;
  footerClassName?: string;
  titleContainerClassName?: string;
  submitLabelClassName?: string;
  cancelLabelClassName?: string;
  cancelLabel?: ReactNode;
  submitLabel?: ReactNode;
  disableSubmit?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const Modal = ({
  id,
  open,
  wrapperClassName,
  className,
  size = "sm",
  titleClassName,
  title,
  contentClassName,
  children,
  footerClassName,
  titleContainerClassName,
  submitLabelClassName,
  cancelLabelClassName,
  cancelLabel,
  submitLabel,
  disableSubmit,
  loading,
  onCancel,
  onSubmit,
}: ModalProps) => {
  const { t } = useTranslationContext();
  if (!open) return null;

  return createPortal(
    <div
      id={id}
      data-testid={id}
      className={cn(
        "w-full h-screen z-[999] fixed bg-black/50 top-0 flex",
        wrapperClassName
      )}
    >
      <div
        data-testid={id ? `content-${id}` : undefined}
        className={cn(
          "w-full absolute max-h-[80%] -translate-y-[50%] max-w-[24rem] -translate-x-[50%] bg-white left-1/2 top-1/2 rounded-lg p-5 overflow-auto",
          size === "sm" && "max-w-[24rem]",
          size === "md" && "max-w-[48rem]",
          size === "2xl" && "max-w-[90rem]",
          className
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between gap-4 border-b border-gray-600 pb-4 mb-4",
            titleContainerClassName
          )}
        >
          <div className={cn("wrap-anywhere", titleClassName)}>{title}</div>
          <div>
            <button
              className="border border-gray-100 h-[2rem] w-[2rem] flex items-center justify-center rounded-md"
              onClick={onCancel}
            >
              <CloseIcon width={14} height={14} />
            </button>
          </div>
        </div>

        <div
          className={cn(
            "mb-4 max-h-[58vh] overflow-y-auto box-border",
            contentClassName
          )}
        >
          {children}
        </div>

        <div
          className={cn(
            "w-full flex items-center justify-end gap-2",
            footerClassName
          )}
        >
          {cancelLabel && (
            <Button
              className={cn("text-base", cancelLabelClassName)}
              variant="outlined"
              onClick={onCancel}
            >
              {cancelLabel || t("cancel")}
            </Button>
          )}

          {submitLabel && (
            <Button
              className={cn("text-base", submitLabelClassName)}
              variant="contained"
              disabled={loading || disableSubmit}
              isLoading={loading}
              onClick={onSubmit}
            >
              {submitLabel}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
